/**
 * <BarcodeScanButton> — camera button next to the product search on the
 * invoice / draft / quotation / sale forms. Owns the scan lifecycle:
 *  1. Open scanner (fresh session — clears recent-scans stack)
 *  2. Look up scanned code → open confirmation dialog
 *  3. On Add → emit `onAddLine(product, qty)` up to the parent, push
 *     onto a local `recentScans` stack (last 3) so the operator can
 *     undo a mis-scan from inside the scanner
 *  4. Continuous scan — reopen the camera automatically (does NOT
 *     clear the stack, unlike a fresh open)
 *
 * Props:
 *   onAddLine(product, qty)   – parent adds the line to its invoice model
 *   onUndoLine(product, qty)  – optional; parent reverses a previous add
 *   summary                   – optional; forwarded to the overlay's
 *                               live invoice summary panel
 *   testId                    – data-testid for the camera button
 */
import { useCallback, useEffect, useState } from 'react';
import { Camera } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { productsAPI, scanDefaultsAPI } from '../../lib/api';
import BarcodeScannerOverlay from './BarcodeScannerOverlay';
import ScanAddDialog from './ScanAddDialog';

const MAX_RECENT = 3;

const BarcodeScanButton = ({
    onAddLine, onUndoLine, summary,
    testId = 'barcode-scan-btn',
}) => {
    const [scannerOpen, setScannerOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [product, setProduct] = useState(null);
    const [notFoundCode, setNotFoundCode] = useState(null);
    const [defaultQty, setDefaultQty] = useState(6);
    // Rolling stack of {id, product, qty, at} — newest first, max 3.
    // NOTE: intentionally NOT reset when `scannerOpen` transitions
    // false → true, because the auto-reopen after each Add would wipe
    // the chips before render (real-device bug on Samsung, Jul 2026).
    // Reset only on manual button click below.
    const [recentScans, setRecentScans] = useState([]);

    useEffect(() => {
        scanDefaultsAPI.get()
            .then((r) => setDefaultQty(r.data?.default_quantity || 6))
            .catch(() => setDefaultQty(6));
    }, []);

    // Fresh scanning session — invoked ONLY when the operator taps
    // the camera button. Continuous scan (dialog → Add → auto-reopen)
    // goes through a different path that preserves the recent-scans
    // stack.
    const openFreshSession = () => {
        setRecentScans([]);
        setScannerOpen(true);
    };

    const handleDetected = useCallback(async (code) => {
        setScannerOpen(false);
        try {
            const { data } = await productsAPI.byBarcode(code);
            setProduct(data);
            setNotFoundCode(null);
            setDialogOpen(true);
            if (navigator.vibrate) navigator.vibrate(30);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 404) {
                setProduct(null);
                setNotFoundCode(code);
                setDialogOpen(true);
            } else {
                toast.error(e?.response?.data?.detail || 'Lookup failed');
                setScannerOpen(true);
            }
        }
    }, []);

    const handleAdd = (p, qty) => {
        setDialogOpen(false);
        setProduct(null);
        onAddLine?.(p, qty);
        // Push onto recent-scans stack (newest first, cap at MAX_RECENT).
        setRecentScans((prev) => [
            { id: `${p.id}-${Date.now()}`, product: p, qty, at: Date.now() },
            ...prev,
        ].slice(0, MAX_RECENT));
        toast.success(`${p.sku} × ${qty}`);
        // Reopen camera for continuous scan (preserves recent-scans stack).
        setTimeout(() => setScannerOpen(true), 150);
    };

    const cancelDialog = () => {
        setDialogOpen(false);
        setProduct(null);
        setNotFoundCode(null);
    };

    const rescan = () => {
        setDialogOpen(false);
        setProduct(null);
        setNotFoundCode(null);
        setTimeout(() => setScannerOpen(true), 100);
    };

    const handleUndoRecent = (entry) => {
        onUndoLine?.(entry.product, entry.qty);
        setRecentScans((prev) => prev.filter((r) => r.id !== entry.id));
        toast.info(`Removed ${entry.product.sku} × ${entry.qty}`);
    };

    return (
        <>
            <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={openFreshSession}
                title="Scan barcode"
                aria-label="Scan barcode"
                data-testid={testId}
                className="shrink-0"
            >
                <Camera size={16} />
            </Button>
            <BarcodeScannerOverlay
                open={scannerOpen}
                onDetected={handleDetected}
                onClose={() => setScannerOpen(false)}
                recentScans={recentScans}
                onUndoScan={onUndoLine ? handleUndoRecent : undefined}
                summary={summary}
            />
            <ScanAddDialog
                open={dialogOpen}
                product={product}
                notFoundCode={notFoundCode}
                defaultQty={defaultQty}
                onAdd={handleAdd}
                onCancel={cancelDialog}
                onRescan={rescan}
            />
        </>
    );
};

export default BarcodeScanButton;
